import jsPDF from 'jspdf';

export function exportConversationToPDF(messages, title = 'Judgy Conversation') {
  const doc = new jsPDF();
  
  // Page settings
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  let yPosition = margin;
  
  // Helper function to add new page if needed
  const checkPageBreak = (neededHeight) => {
    if (yPosition + neededHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };
  
  // Load and add logo image
  const addLogo = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function() {
        const logoHeight = 25;
        const aspectRatio = img.width / img.height;
        const logoWidth = logoHeight * aspectRatio;
        
        doc.addImage(img, 'PNG', margin, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 15;
        resolve();
      };
      img.onerror = function() {
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(25, 118, 210);
        doc.text('Judgy', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text('If you can\'t ask a judge, ask Judgy', margin, yPosition);
        yPosition += 15;
        resolve();
      };
      img.src = '/ExportPDF.png';
    });
  };
  
  // Helper to process a single line with markdown and card links
  const processLine = (text) => {
    // First, handle card links [[Card Name]]
    const cardRegex = /\[\[([^\]]+)\]\]/g;
    let segments = [];
    let lastIndex = 0;
    let match;
    
    while ((match = cardRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          text: text.substring(lastIndex, match.index),
          isCard: false
        });
      }
      segments.push({
        text: match[1],
        isCard: true,
        url: `https://scryfall.com/search?q=!"${encodeURIComponent(match[1])}"`
      });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      segments.push({
        text: text.substring(lastIndex),
        isCard: false
      });
    }
    
    if (segments.length === 0) {
      segments.push({ text: text, isCard: false });
    }
    
    // Now process each segment for bold markdown
    const processedSegments = [];
    for (let segment of segments) {
      if (segment.isCard) {
        processedSegments.push(segment);
        continue;
      }
      
      // Handle bold **text**
      const boldRegex = /\*\*(.+?)\*\*/g;
      let parts = [];
      let lastIdx = 0;
      let boldMatch;
      
      while ((boldMatch = boldRegex.exec(segment.text)) !== null) {
        if (boldMatch.index > lastIdx) {
          parts.push({
            text: segment.text.substring(lastIdx, boldMatch.index),
            bold: false,
            isCard: false
          });
        }
        parts.push({
          text: boldMatch[1],
          bold: true,
          isCard: false
        });
        lastIdx = boldMatch.index + boldMatch[0].length;
      }
      
      if (lastIdx < segment.text.length) {
        parts.push({
          text: segment.text.substring(lastIdx),
          bold: false,
          isCard: false
        });
      }
      
      if (parts.length === 0) {
        parts.push({
          text: segment.text,
          bold: false,
          isCard: false
        });
      }
      
      processedSegments.push(...parts);
    }
    
    return processedSegments;
  };
  
  // Helper to render a line of text segments
  const renderLine = (segments, x, y, maxWidth, fontSize) => {
    let currentX = x;
    let currentY = y;
    
    for (let segment of segments) {
      if (!segment.text) continue;
      
      doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      
      // Split into words to prevent awkward breaks
      const words = segment.text.split(' ');
      
      for (let i = 0; i < words.length; i++) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '');
        const wordWidth = doc.getTextWidth(word);
        
        // Check if word fits on current line
        if (currentX + wordWidth > x + maxWidth && currentX > x) {
          // Move to next line
          checkPageBreak(fontSize * 0.6);
          currentY += fontSize * 0.6;
          currentX = x;
        }
        
        // Render word
        if (segment.isCard) {
          doc.setTextColor(25, 118, 210);
          doc.textWithLink(word, currentX, currentY, { url: segment.url });
          doc.setTextColor(0, 0, 0);
        } else {
          doc.text(word, currentX, currentY);
        }
        
        currentX += wordWidth;
      }
    }
    
    return currentY;
  };
  
  // Parse markdown lines
  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const parsed = [];
    
    for (let line of lines) {
      const trimmed = line.trim();
      
      // Check if it's a bullet (starts with - or * but not part of **markdown**)
      const isBullet = (trimmed.startsWith('- ') || trimmed.startsWith('* ')) && 
                       !trimmed.startsWith('**');
      
      if (isBullet) {
        const content = trimmed.substring(2); // Remove "- " or "* "
        parsed.push({
          type: 'bullet',
          segments: processLine(content)
        });
      } else if (trimmed === '') {
        parsed.push({ type: 'space' });
      } else {
        parsed.push({
          type: 'normal',
          segments: processLine(line)
        });
      }
    }
    
    return parsed;
  };
  
  // Render parsed markdown
  const renderParsedText = (parsedLines, baseX, maxWidth, fontSize) => {
    for (let lineObj of parsedLines) {
      if (lineObj.type === 'space') {
        yPosition += fontSize * 0.4;
        continue;
      }
      
      let x = baseX;
      
      if (lineObj.type === 'bullet') {
        checkPageBreak(fontSize * 0.6);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fontSize);
        doc.text('•', x, yPosition);
        x += 7; // Indent after bullet
        
        yPosition = renderLine(lineObj.segments, x, yPosition, maxWidth - 7, fontSize);
        yPosition += fontSize * 0.6;
      } else {
        checkPageBreak(fontSize * 0.6);
        yPosition = renderLine(lineObj.segments, x, yPosition, maxWidth, fontSize);
        yPosition += fontSize * 0.6;
      }
    }
  };
  
  // Main export function
  const generatePDF = async () => {
    await addLogo();
    
    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    const titleLines = doc.splitTextToSize(title, maxWidth);
    doc.text(titleLines, margin, yPosition);
    yPosition += titleLines.length * 8 + 5;
    
    // Add date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const date = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(date, margin, yPosition);
    yPosition += 15;
    
    // Separator
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
    
    // Process messages
    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      const isUser = message.sender === 'user';
      
      checkPageBreak(15);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(isUser ? 25 : 76, isUser ? 118 : 175, isUser ? 210 : 80);
      doc.text(isUser ? 'Q:' : 'A:', margin, yPosition);
      yPosition += 7;
      
      doc.setTextColor(0, 0, 0);
      const parsedLines = parseMarkdown(message.text);
      renderParsedText(parsedLines, margin + 5, maxWidth - 5, 11);
      
      yPosition += 5;
      
      if (i < messages.length - 1) {
        checkPageBreak(5);
        doc.setDrawColor(230, 230, 230);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
      }
    }
    
    // Footer
    yPosition = pageHeight - 15;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    const footer = 'Generated by Judgy (askjudgy.com) - For casual/educational use only';
    const footerWidth = doc.getTextWidth(footer);
    doc.text(footer, (pageWidth - footerWidth) / 2, yPosition);
    
    const filename = `${title.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
    doc.save(filename);
  };
  
  generatePDF();
}